import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import tw, { styled } from 'twin.macro';
import { EyeSlashIcon } from '@heroicons/react/24/solid';
import { RadioGroup as HeadlessRadioGroup } from '@headlessui/react';

import { BaseModal, ButtonV2, Title } from '@restworld/ui-ds';
import { Select, SelectOptionType } from '@restworld/ui-ds-base';
import { NPSFeedbackData } from '../hooks/use-put-offline-job-position';

interface PutOfflineJobPositionNPSModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: NPSFeedbackData) => void;
  isLoading?: boolean;
}

const NPS_SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Threshold: NPS 1-8 = mostra domanda "cosa è andato storto"
//            NPS 9-10 = mostra domanda "quando riattiverai"
const NPS_THRESHOLD = 8;

const MOTIVE_OPTIONS = [
  { key: 'few_candidates', label: 'nps_motive_few_candidates', value: 'few_candidates' },
  { key: 'candidates_not_suitable', label: 'nps_motive_candidates_not_suitable', value: 'candidates_not_suitable' },
  { key: 'service_too_slow', label: 'nps_motive_service_too_slow', value: 'service_too_slow' },
  { key: 'too_expensive', label: 'nps_motive_too_expensive', value: 'too_expensive' },
  { key: 'platform_difficult', label: 'nps_motive_platform_difficult', value: 'platform_difficult' },
  { key: 'support_insufficient', label: 'nps_motive_support_insufficient', value: 'support_insufficient' },
  { key: 'hired_elsewhere', label: 'nps_motive_hired_elsewhere', value: 'hired_elsewhere' },
  { key: 'other', label: 'nps_motive_other', value: 'other' },
];

const REACTIVATION_OPTIONS = [
  { key: 'within_1_month', label: 'nps_reactivation_within_1_month', value: 'within_1_month' },
  { key: 'within_3_months', label: 'nps_reactivation_within_3_months', value: 'within_3_months' },
  { key: 'within_6_months', label: 'nps_reactivation_within_6_months', value: 'within_6_months' },
  { key: 'more_than_6_months', label: 'nps_reactivation_more_than_6_months', value: 'more_than_6_months' },
  { key: 'not_sure', label: 'nps_reactivation_not_sure', value: 'not_sure' },
];

export function PutOfflineJobPositionNPSModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}: PutOfflineJobPositionNPSModalProps) {
  const { t } = useTranslation('job-positions-page');

  const [npsScore, setNpsScore] = useState<string | null>(null);
  const [motiveForCancelling, setMotiveForCancelling] = useState<SelectOptionType | undefined>(undefined);
  const [reactivationDate, setReactivationDate] = useState<SelectOptionType | undefined>(undefined);

  const showMotiveQuestion = npsScore !== null && Number(npsScore) <= NPS_THRESHOLD;
  const showReactivationQuestion = npsScore !== null && Number(npsScore) > NPS_THRESHOLD;

  // Reset answers when NPS score changes
  useEffect(() => {
    if (!showReactivationQuestion && reactivationDate) {
      setReactivationDate(undefined);
    }
    if (!showMotiveQuestion && motiveForCancelling) {
      setMotiveForCancelling(undefined);
    }
  }, [showReactivationQuestion, showMotiveQuestion, reactivationDate, motiveForCancelling]);

  const handleSubmit = useCallback(() => {
    // Validazione: NPS obbligatorio + domanda condizionale in base al punteggio
    if (!npsScore) return;
    if (showMotiveQuestion && !motiveForCancelling?.value) return;
    if (showReactivationQuestion && !reactivationDate?.value) return;

    onSubmit({
      npsScore: Number(npsScore),
      motiveForCancelling: showMotiveQuestion ? motiveForCancelling?.value : undefined,
      reactivationDate: showReactivationQuestion ? reactivationDate?.value : undefined,
    });
  }, [npsScore, motiveForCancelling, reactivationDate, showMotiveQuestion, showReactivationQuestion, onSubmit]);

  const isFormValid =
    npsScore &&
    (!showMotiveQuestion || motiveForCancelling?.value) &&
    (!showReactivationQuestion || reactivationDate?.value);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setNpsScore(null);
      setMotiveForCancelling(undefined);
      setReactivationDate(undefined);
      onClose();
    }
  }, [isLoading, onClose]);

  const titleEl = (
    <Title
      content={t('nps_modal_title')}
      fontSize="extraLarge"
      fontWeight="semibold"
      textColor="neutral-700"
      twStyle={tw`text-center leading-[30px] px-4 sm:px-0`}
    />
  );

  const footerEl = (
    <div tw="flex flex-col justify-center items-center gap-4 w-full">
      <ButtonV2
        onClick={handleSubmit}
        fullwidth
        loading={isLoading}
        StartIcon={EyeSlashIcon}
        variant="secondary-danger"
        disabled={isLoading || !isFormValid}
      >
        {t('nps_confirm_and_go_offline_button')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" fullwidth onClick={handleClose} disabled={isLoading}>
        {t('nps_keep_listing_online_button')}
      </ButtonV2>
    </div>
  );

  return (
    <BaseModal
      showMobileGestures
      modalStyles={[tw`w-full max-w-[640px] lg:(max-w-screen-sm)`]}
      open={isOpen}
      onClose={handleClose}
      title={titleEl}
      footer={footerEl}
    >
      <div tw="flex flex-col gap-6 w-full mt-6">
        {/* NPS Score Question */}
        <div tw="space-y-3">
          <label tw="block text-base font-medium text-neutral-700 mb-2">
            {t('nps_question_recommend')}
            <span tw="text-red-500 ml-1">*</span>
          </label>
          <div tw="flex flex-col items-start w-full">
            <HeadlessRadioGroup
              value={npsScore}
              onChange={(newValue) => setNpsScore(newValue)}
              tw="flex flex-row justify-between items-center gap-1 w-full"
            >
              {NPS_SCORES.map((score) => {
                const scoreStr = score.toString();
                return (
                  <HeadlessRadioGroup.Option key={score} value={scoreStr} tw="flex-1">
                    {({ checked }) => (
                      <NPSButton score={score} checked={checked}>
                        {scoreStr}
                      </NPSButton>
                    )}
                  </HeadlessRadioGroup.Option>
                );
              })}
            </HeadlessRadioGroup>
            {/* Legenda NPS */}
            <NPSLegend tw="mt-3">
              <LegendItem>
                <LegendLabel>1-6</LegendLabel>
                <LegendText>→ {t('nps_legend_1_6')}</LegendText>
              </LegendItem>
              <LegendItem>
                <LegendLabel>7-8</LegendLabel>
                <LegendText>→ {t('nps_legend_7_8')}</LegendText>
              </LegendItem>
              <LegendItem>
                <LegendLabel>9-10</LegendLabel>
                <LegendText>→ {t('nps_legend_9_10')}</LegendText>
              </LegendItem>
            </NPSLegend>
          </div>
        </div>

        {/* Motive for Cancelling Question - SOLO se NPS <= 8 */}
        {npsScore !== null && Number(npsScore) <= NPS_THRESHOLD && (
          <div tw="space-y-3">
            <label htmlFor="motive" tw="block text-base font-medium text-neutral-700 mb-2">
              {t('nps_question_what_didnt_work')}
              <span tw="text-red-500 ml-1">*</span>
            </label>
            <Select
              fullwidth
              options={MOTIVE_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
              placeholder={t('nps_select_option')}
              value={motiveForCancelling}
              onChange={(newValue) => setMotiveForCancelling(newValue)}
            />
          </div>
        )}

        {/* Reactivation Date Question (conditional on NPS >= 7) */}
        {showReactivationQuestion && (
          <div tw="space-y-3">
            <label htmlFor="reactivation" tw="block text-base font-medium text-neutral-700 mb-2">
              {t('nps_question_when_need_staff_again')}
              <span tw="text-red-500 ml-1">*</span>
            </label>
            <Select
              fullwidth
              options={REACTIVATION_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
              placeholder={t('nps_select_option')}
              value={reactivationDate}
              onChange={(newValue) => setReactivationDate(newValue)}
            />
          </div>
        )}
      </div>
    </BaseModal>
  );
}

// Styled component come in SubscriptionCancellationFeedback
const HelperText = styled.span`
  ${tw`text-sm text-neutral-100 block`}
`;

// Componenti per la legenda NPS
const NPSLegend = styled.div`
  ${tw`w-full border border-neutral-40 rounded-lg p-2 flex flex-col gap-1`}
`;

const LegendItem = styled.div`
  ${tw`flex items-start gap-1 text-sm text-neutral-200`}
`;

const LegendLabel = styled.span`
  ${tw`font-bold whitespace-nowrap`}
`;

const LegendText = styled.span`
  ${tw`font-normal`}
`;

// Custom NPS Button component che replica esattamente il Figma
const NPSButton = styled.button<{ score: number; checked: boolean }>`
  ${tw`
    flex items-center justify-center
    border border-solid rounded-lg
    font-medium text-base leading-normal
    cursor-pointer
    transition-colors
    h-12 px-3
    w-full
  `}

  ${({ score, checked }) => {
    if (score >= 9) {
      return checked
        ? tw`bg-green-700 text-neutral-10 border-green-700 hover:bg-green-700`
        : tw`bg-green-50 text-green-700 border-green-700 hover:bg-green-100`;
    } else if (score >= 7) {
      return checked
        ? tw`bg-orange-700 text-neutral-10 border-orange-700 hover:bg-orange-700`
        : tw`bg-orange-50 text-orange-700 border-orange-700 hover:bg-orange-100`;
    } else {
      return checked
        ? tw`bg-red-700 text-neutral-10 border-red-700 hover:bg-red-700`
        : tw`bg-red-50 text-red-700 border-red-700 hover:bg-red-100`;
    }
  }}
`;

export default PutOfflineJobPositionNPSModal;
